import { useState, useCallback } from 'react';
import {
  products as productsApi,
  product as productApi,
  inventoryProduct
} from 'utilities/api';
import catchCancel from 'helpers/catchCancel';
import { getCompatibleUnits } from 'helpers/unitConversionHelpers';
import { getRegistrationNumber } from 'screens/ProductList/helpers/filteredProducts';
import useUomConvert from 'hooks/useUomConvert';

const useProduct = () => {
  const convertFn = useUomConvert();
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const getProduct = useCallback(id => {
    setIsLoading(true);
    setError(null);
    const { promise } = inventoryProduct.fetch(id);

    return promise
      .then(({ data }) => {
        setProduct(data);

        return data;
      })
      .catch(err => {
        setProduct(null);
        setError(err);
        return null;
      })
      .finally(() => setIsLoading(false));
  }, []);

  const getMasterDataProduct = useCallback(id => {
    setIsLoading(true);
    setError(null);
    const { promise } = productApi.fetch(id);

    return promise
      .then(({ data }) => {
        setProduct(data);
        return data;
      })
      .catch(err => {
        setProduct(null);
        setError(err);
        return null;
      })
      .finally(() => setIsLoading(false));
  }, []);

  const getMasterListProduct = useCallback(id => {
    setIsLoading(true);
    setError(null);
    const { promise } = productsApi.fetch(id);

    return promise
      .then(({ data }) => {
        setProduct(data);
        return data;
      })
      .catch(err => {
        setProduct(null);
        setError(err);
        return null;
      })
      .finally(() => setIsLoading(false));
  }, []);

  const getMasterListProductUnits = async id => {
    setIsLoading(true);
    setError(null);
    const { promise } = productsApi.fetch(id);

    return promise
      .then(response => {
        const { stdunit, stdpackageunit, density } = response.data;

        return getCompatibleUnits(
          [stdunit, stdpackageunit],
          density,
          convertFn
        );
      })
      .catch(catchCancel)
      .catch(err => {
        setError(err);
        return [];
      })
      .finally(() => setIsLoading(false));
  };

  const getProductUnits = async id => {
    setIsLoading(true);
    setError(null);
    const { promise } = productApi.fetch(id);

    return promise
      .then(response => {
        const { standardUnit, stdPackageUnit, density } = response.data;

        return getCompatibleUnits(
          [standardUnit, stdPackageUnit],
          density,
          convertFn
        );
      })
      .catch(catchCancel)
      .catch(err => {
        setError(err);
        return [];
      })
      .finally(() => setIsLoading(false));
  };

  const fetchProductData = useCallback(
    async previousValues => {
      if (previousValues.custom) {
        const masterDataProduct = await getMasterDataProduct(
          previousValues.productId
        );
        if (masterDataProduct) {
          return {
            productId: previousValues.productId,
            data: {
              ...previousValues,
              stdunit: masterDataProduct.standardUnit,
              stdpackageunit: masterDataProduct.stdPackageUnit,
              density: masterDataProduct.density,
              stdfactor: masterDataProduct.stdFactor,
              name: masterDataProduct.name,
              manufacturer: masterDataProduct.manufacturer,
              productType: masterDataProduct.type,
              registrationNumber: getRegistrationNumber(masterDataProduct)
            }
          };
        }
      } else {
        const masterListProduct = await getMasterListProduct(
          previousValues.productId
        );
        if (masterListProduct) {
          return {
            productId: previousValues.productId,
            data: {
              ...previousValues,
              stdunit: masterListProduct.stdunit,
              stdpackageunit: masterListProduct.stdpackageunit,
              density: masterListProduct.density,
              stdfactor: masterListProduct.stdfactor,
              name: masterListProduct.name,
              manufacturer: masterListProduct.manufacturer,
              productType: masterListProduct.producttype,
              registrationNumber: getRegistrationNumber(masterListProduct)
            }
          };
        }
      }
      return null;
    },
    [getMasterDataProduct, getMasterListProduct]
  );

  return {
    getMasterListProductUnits,
    getProductUnits,
    getProduct,
    getMasterListProduct,
    fetchProductData,
    isLoading,
    error,
    product
  };
};

export default useProduct;
